import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ProductCard from './ProductCard';
import { getCategoryIcon } from './CustomIcons';
import { productService } from '../services/firebaseService';
import './ProductCategories.css';

// --- Dynamic Color Palette ---
// Using colors inspired by the existing theme (e.g., from AboutUs.js CSS)
const COLOR_PALETTE = [
  '#6bbe44', // Accent green
  '#0073b1', // Primary blue
  '#e57373', // Red from existing categoryColors
  '#81c784', // Green from existing categoryColors
  '#64b5f6', // Light blue from existing categoryColors
  '#ba68c8', // Purple from existing categoryColors
  '#ffb74d', // Orange
  '#4db6ac', // Teal
  '#7986cb', // Indigo
  '#a1887f', // Brown
];

const getDynamicCategoryColor = (categoryName) => {
  if (!categoryName) return '#ccc';
  const hash = categoryName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return COLOR_PALETTE[hash % COLOR_PALETTE.length];
};

const getUniqueValues = (data, key) => {
  const values = [];
  if (!data || data.length === 0) return [];

  data.forEach(product => {
    if (product && product[key]) {
      if (Array.isArray(product[key])) {
        values.push(...product[key]);
      } else {
        values.push(product[key]);
      }
    }
  });
  // Using a Map to preserve original casing while ensuring uniqueness based on lowercase
  const uniqueCaseInsensitiveValues = new Map();
  values.forEach(value => {
    const lowerCaseValue = String(value).toLowerCase();
    if (!uniqueCaseInsensitiveValues.has(lowerCaseValue)) {
      uniqueCaseInsensitiveValues.set(lowerCaseValue, value);
    }
  });
  return Array.from(uniqueCaseInsensitiveValues.values()).sort();
};

const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
  </svg>
);

const GridIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25A2.25 2.25 0 0 1 13.5 8.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z" />
  </svg>
);

const ListIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
  </svg>
);

function ProductCategories() {
  const { categoryName } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({ origin: 'all', packaging: 'all' });
  const [sortBy, setSortBy] = useState('name-asc');
  const [view, setView] = useState('grid');
  // Global pagination for single category view
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(5); // Changed to 5 products per page
  
  // Per-category pagination for 'All Products' view
  const [categoryPaginationStates, setCategoryPaginationStates] = useState({});

  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const activeCategory = categoryName ? categoryName.toLowerCase() : 'all';
  const pageTitle = categoryName ? categoryName : 'All Products';

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      try {
        const products = await productService.getAllProducts();
        if (products && products.length > 0) {
          setAllProducts(products);
        } else {
          setAllProducts([]);
        }
      } catch (error) {
        console.error('Error loading products:', error);
        setAllProducts([]); 
      }
      setLoading(false);
    };

    loadProducts();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset global pagination on filter change
  };
  
  // Reset search and filters when navigating between categories
  React.useEffect(() => {
    setSearchQuery('');
    setFilters({ origin: 'all', packaging: 'all' });
    setCurrentPage(1); // Reset global pagination
    setCategoryPaginationStates({}); // Reset per-category pagination
  }, [categoryName]);

  const filteredProducts = useMemo(() => {

    let products = [...allProducts];
    
    // Only apply category filter if activeCategory is NOT 'all'
    if (activeCategory !== 'all') {
      products = products.filter(p => p.category && String(p.category).toLowerCase() === activeCategory);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      products = products.filter(p => 
        (p.name && String(p.name).toLowerCase().includes(query)) || 
        (p.scientific && String(p.scientific).toLowerCase().includes(query)) ||
        (p.description && String(p.description).toLowerCase().includes(query))
      );
    }
    
    if (filters.origin !== 'all') {
      products = products.filter(p => p.origin && String(p.origin).toLowerCase() === filters.origin.toLowerCase());
    }
    
    if (filters.packaging !== 'all') {
      products = products.filter(p => p.packaging && Array.isArray(p.packaging) && 
        p.packaging.some(pkg => String(pkg).toLowerCase() === filters.packaging.toLowerCase()));
    }
    
    products.sort((a, b) => {
      const nameA = a.name || '';
      const nameB = b.name || '';
      const originA = a.origin || '';
      const originB = b.origin || '';

      if (sortBy === 'name-asc') return nameA.localeCompare(nameB);
      if (sortBy === 'origin-asc') return originA.localeCompare(originB);
      return 0;
    });
    
    return products;
  }, [searchQuery, filters, sortBy, activeCategory, allProducts]);

  // Handle pagination for individual category when activeCategory is 'all'
  const handleCategoryPaginate = (category, pageNumber) => {
    setCategoryPaginationStates(prev => ({
      ...prev,
      [category]: { ...prev[category], currentPage: pageNumber }
    }));
  };

  const getPaginationRange = (totalPages, currentPage) => {
    const range = [], showPages = 5;
    let start = Math.max(1, currentPage - Math.floor(showPages / 2));
    let end = Math.min(totalPages, start + showPages - 1);
    if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
    for (let i = start; i <= end; i++) range.push(i);
    return range;
  };

  const groupedProducts = useMemo(() => {

    if (activeCategory !== 'all') {
      // For a specific category, apply global pagination to the filtered products
      const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
      const indexOfLastProduct = currentPage * productsPerPage;
      const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
      const productsForDisplay = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);

      return [{
        name: categoryName, // Use original categoryName for display
        products: productsForDisplay,
        currentPage: currentPage,
        totalPages: totalPages,
        paginate: (page) => setCurrentPage(page) // Use global setter
      }];
    }
    
    // When activeCategory is 'all', group by category and apply per-category pagination
    const categoriesMap = {};
    filteredProducts.forEach(product => {
      if (product.category) { 
        if (!categoriesMap[product.category]) {
          categoriesMap[product.category] = [];
        }
        categoriesMap[product.category].push(product);
      }
    });
    
    const result = Object.entries(categoriesMap).map(([name, products]) => {
      const categoryCurrentPage = categoryPaginationStates[name]?.currentPage || 1;
      const categoryTotalPages = Math.ceil(products.length / productsPerPage);
      const indexOfLastProduct = categoryCurrentPage * productsPerPage;
      const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
      const productsForDisplay = products.slice(indexOfFirstProduct, indexOfLastProduct);

      return {
        name,
        products: productsForDisplay,
        currentPage: categoryCurrentPage,
        totalPages: categoryTotalPages,
        paginate: (page) => handleCategoryPaginate(name, page) // Use per-category setter
      };
    });

    return result;
  }, [filteredProducts, activeCategory, categoryName, currentPage, productsPerPage, categoryPaginationStates]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px', fontSize: '1.2rem' }}>
        Loading products...
      </div>
    );
  }

  return (
    <div className="products-page-container">
      <div className="page-header">
        <h1 className="products-page-title">
          <span className="title-gradient">{pageTitle}</span>
        </h1>
        <p className="products-subtitle">
          Discover our curated collection of {filteredProducts.length} premium products
        </p>
      </div>

      
      
      <div className="product-categories-container">
        {groupedProducts.length > 0 ? (
          groupedProducts.map((category) => (
            <section key={category.name} className="product-category" 
              style={{'--category-color': getDynamicCategoryColor(category.name)}}>
              <h3 className="category-heading">
                <span className="category-icon" aria-hidden="true">
                  {React.createElement(getCategoryIcon(category.name), { size: 40, color: 'currentColor' })}
                </span>
                <span className="category-name">{category.name}</span>
                <span className="category-count">{category.products.length}</span>
              </h3>
              <div className={`product-grid ${view}-view`}>
                {category.products.map((product) => (
                  <ProductCard key={product.id || product.name} product={product} view={view} />
                ))}
              </div>

              {/* Per-category pagination for 'All Products' view or global pagination for specific category */}
              {category.totalPages > 1 && (
                <div className="pagination">
                {/* PREVIOUS BUTTON */}
                <button 
                  onClick={() => {
                    category.paginate(category.currentPage - 1);
                    window.scrollTo({ top: 0, behavior: 'smooth' }); // <--- ADD THIS
                  }} 
                  disabled={category.currentPage === 1}
                  className="pagination-arrow" 
                  aria-label="Previous page"
                >
                  ←
                </button>
              
                {/* NUMBER BUTTONS */}
                {getPaginationRange(category.totalPages, category.currentPage).map(number => (
                  <button 
                    key={number} 
                    onClick={() => {
                      category.paginate(number);
                      window.scrollTo({ top: 0, behavior: 'smooth' }); // <--- ADD THIS
                    }}
                    className={category.currentPage === number ? 'active' : ''}
                  >
                    {number}
                  </button>
                ))}
              
                {/* NEXT BUTTON */}
                <button 
                  onClick={() => {
                    category.paginate(category.currentPage + 1);
                    window.scrollTo({ top: 0, behavior: 'smooth' }); // <--- ADD THIS
                  }} 
                  disabled={category.currentPage === category.totalPages}
                  className="pagination-arrow" 
                  aria-label="Next page"
                >
                  →
                </button>
              </div>
              )}
            </section>
          ))
        ) : (
          <div className="no-results">
            <div className="no-results-icon">🔍</div>
            <h3>No products found</h3>
            <p>Try adjusting your search or filter settings to find what you're looking for.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductCategories;